"use client";

import { usePathname } from "next/navigation";
import { ReactNode, useState } from "react";
import type { MenuItem } from "shared";

type SubMenuProps = {
  children: ReactNode;
  heading: string;
  logo: ReactNode;
  menuItems: MenuItem[];
};

export function SubMenu({ children, heading, logo, menuItems }: SubMenuProps) {
  const [navbarOpen, setNavbarOpen] = useState(false);
  const pathname = usePathname();

  const navbarToggleHandler = () => {
    setNavbarOpen(!navbarOpen);
  };

  const isActive = (path?: string) => {
    if (!path) return false;
    return pathname === path;
  };

  return (
    <div className="flex flex-col lg:flex-row font-roboto">
      <header className="bg-white px-4 lg:w-[244px] lg:flex-none shadow-md lg:shadow-xl flex flex-row lg:flex-col lg:min-h-screen justify-between lg:justify-start items-center relative print:hidden">
        <div className="flex items-center justify-between w-full py-4 lg:py-0 lg:flex-col">
          <div className="hidden lg:block lg:mt-[40px] lg:mb-4">{logo}</div>
          <h1 className="text-2xl lg:text-3xl lg:text-center text-scoutBlue lg:w-full lg:border-y-2 lg:border-y-scoutBlue lg:my-4 lg:py-2">
            {heading}
          </h1>

          {/* Mobile Menu Button */}
          <button
            onClick={navbarToggleHandler}
            aria-label="Mobile Menu"
            className="lg:hidden p-2"
          >
            <span
              className={`relative my-1.5 block h-0.5 w-[24px] bg-csblue transition-all duration-300 ${
                navbarOpen ? "top-[7px] rotate-45" : ""
              }`}
            />
            <span
              className={`relative my-1.5 block h-0.5 w-[24px] bg-csblue transition-all duration-300 ${
                navbarOpen ? "opacity-0" : ""
              }`}
            />
            <span
              className={`relative my-1.5 block h-0.5 w-[24px] bg-csblue transition-all duration-300 ${
                navbarOpen ? "top-[-8px] -rotate-45" : ""
              }`}
            />
          </button>
        </div>

        <nav
          className={`navbar w-full lg:block ${
            navbarOpen
              ? "absolute top-full left-0 right-0 bg-white shadow-md z-30 border-t border-gray-200"
              : "hidden"
          }`}
        >
          <ul className="block lg:space-y-1">
            {menuItems?.map((menuItem) => (
              <li key={menuItem.id} className="group relative">
                {menuItem.path ? (
                  <>
                    <a
                      href={menuItem.path}
                      target={menuItem.newTab ? "_blank" : undefined}
                      rel={menuItem.newTab ? "noopener noreferrer" : undefined}
                      className={`
                        flex py-3 px-4 lg:px-4 text-black text-lg lg:text-xl
                        font-medium hover:bg-gray-50 lg:hover:bg-gray-50
                        dark:text-white transition-colors
                        ${isActive(menuItem.path) ? "text-primary bg-gray-100" : ""}
                        ${menuItem.highlight ? "bg-blue-50 text-blue-800 hover:bg-blue-100" : ""}
                      `}
                    >
                      {menuItem.title}
                    </a>
                    {/* Add divider if specified - only visible on desktop */}
                    {menuItem.dividerAfter && (
                      <div className="hidden lg:block h-px bg-gray-200 my-3 mx-2"></div>
                    )}
                  </>
                ) : null}
              </li>
            ))}
          </ul>
        </nav>
      </header>
      <div className="w-full py-8 px-4 lg:px-8 print:py-0 print:px-0">
        {children}
      </div>
    </div>
  );
}
